import React from 'react';
import { Tooltip } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';

import {
	HI_ASSOCIATED_NAME_HELP_ID,
	HI_ASSOCIATED_NAME_HELP_TEXT,
	HI_ASSOCIATED_NAME_LABEL,
} from './patientNameDisplay';

/**
 * B1.4-E — read-only indication that a name is the one MyGene recorded as used in
 * an approved verified-IHI interaction (HI_005_005804 / HI_010_005804 /
 * HI_015_005804).
 *
 * Presentation rules:
 *
 *  - text AND icon, never colour alone: the label is always rendered, so the
 *    indication survives greyscale, high-contrast and colour-blind viewing;
 *  - read-only. There is no control to set or clear association state — it is
 *    established only by an approved server-side verified-IHI path;
 *  - bounded. Only the derived boolean is displayed. No association timestamp,
 *    verified path, source or trust value, provider content, ownership internal
 *    or raw IHI value is exposed, and nothing here asserts what the HI Service
 *    currently holds.
 *
 * Accessibility: the label is described by the single shared help text the
 * containing section renders once, so assistive technology reads the meaning
 * without the tooltip being the only route to it.
 */
const PatientHiAssociatedIndicator: React.FC<{ describedById?: string }> = ({
	describedById = HI_ASSOCIATED_NAME_HELP_ID,
}) => (
	<Tooltip title={HI_ASSOCIATED_NAME_HELP_TEXT}>
		<span
			aria-describedby={describedById}
			style={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: 4,
				fontSize: 11,
				fontWeight: 600,
				padding: '0 6px',
				borderRadius: 3,
				border: '1px solid var(--mg-border)',
				color: 'var(--mg-text)',
			}}
		>
			<SafetyCertificateOutlined aria-hidden="true" />
			{HI_ASSOCIATED_NAME_LABEL}
		</span>
	</Tooltip>
);

export default PatientHiAssociatedIndicator;
